import React, { useRef, useState } from 'react';
import type { KeyboardEventHandler, ReactElement } from 'react';
import type { MultiLocationSuggestOption, MultiAutocompleteProps } from '@construct-kit/core';
import { spacingHelper, paletteHelper, Switch, typographyHelper } from '@construct-kit/core';
import styled, { css } from 'styled-components';
import { fetchLocationSuggestions, locationTypeArray } from '../fetchLocationSuggestions';
import { largeScreenBtmBorderRadius, StyledMultiLocationSuggest } from './locationSearchStyles';
import { customMapOptionToListNode, mapOptionsToListboxNode } from '../locationSuggestUtils';
import type {
  ExtendedMultiLocationSuggestOption,
  SelectedFiltersOptionsType,
} from '../model/constants/filterTypes';
import { getLocationSearchRecentLocations } from '../../routes/recentSearchesService';

const StyledSwitch = styled(Switch)`
  padding-top: 5px;
  .label {
    ${typographyHelper('body01')}
  }
`;

const SwitchWrapper = styled.div<{ top: number; showRecentLocations: boolean }>`
  position: absolute;
  ${({ top }) => (top ? `top: ${top}px;` : 'display: none')}
  border-bottom: ${({ theme }) => theme.border.width.thin} solid
    ${paletteHelper('borderSecondary')};
  padding: ${spacingHelper('small medium')};
  width: 100%;
  ${({ theme }) => theme.layoutBreakpoints.large} {
    background: ${paletteHelper('backgroundPrimary')};
    box-shadow: 0 0.0625rem 0.25rem 0 rgb(0 0 0 / 16%);
    width: auto;
    right: 0;
    left: 0;
    ${largeScreenBtmBorderRadius};
    ${({ showRecentLocations }) =>
    showRecentLocations &&
      css`
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      `}
  }
`;

export interface ListingLocationSuggestProps
  extends Omit<MultiAutocompleteProps, 'options' | 'label'> {
  className: string;
  setSelectedFiltersOptions: (value: SelectedFiltersOptionsType) => void;
  selectedFiltersOptions: SelectedFiltersOptionsType;
  shouldHideSwitch?: boolean;
  icon?: React.ReactElement;
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>;
}

const totalOptionsToDisplay = 7;

export const ListingLocationSuggest = ({
  selectedFiltersOptions,
  setSelectedFiltersOptions,
  shouldHideSwitch,
  icon,
  onKeyPress,
  ...props
}: ListingLocationSuggestProps): ReactElement => {
  const selectedSearchLocations = selectedFiltersOptions.searchLocations;
  const [addressSuggestions, setAddressSuggestions] = useState<Array<MultiLocationSuggestOption>>(
    [],
  );
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [switchTop, setSwitchTop] = useState(0);

  const refCallback = (node: HTMLInputElement) => {
    inputRef.current = node;

    node &&
      setSwitchTop(
        (node.offsetParent as HTMLDivElement)?.offsetHeight
          ? (node.offsetParent as HTMLDivElement).offsetHeight - 6
          : 0,
      );
  };

  const handleSurroundingSuburbOnChange = (includeSurroundingSuburb?: boolean): void => {
    setSelectedFiltersOptions({
      ...selectedFiltersOptions,
      includeSurroundingSuburbs: includeSurroundingSuburb,
    });
  };

  const filterSuggestions = (fetchedSuggestions: MultiLocationSuggestOption[]) =>
    fetchedSuggestions.filter(
      (suggestion) =>
        !selectedSearchLocations?.some(
          (selectedSearchLocation) =>
            selectedSearchLocation.display.text === suggestion.display.text.replace(/[\s-]+/g, ' '),
        ),
    );

  const onInputChange = async (inputValue: string) => {
    const params = {
      maxResults: totalOptionsToDisplay,
      locationTypes: locationTypeArray,
      locationQuery: inputValue,
    };
    const fetchedSuggestions: Array<MultiLocationSuggestOption> | undefined =
      await fetchLocationSuggestions(params);

    if (fetchedSuggestions) {
      const suggestions = filterSuggestions(fetchedSuggestions).slice(0, totalOptionsToDisplay);
      const updatedAddressSuggestions =
        inputValue !== ''
          ? [
            ...suggestions,
            {
              display: {
                text: inputValue,
              },
            },
          ]
          : suggestions;

      setAddressSuggestions(updatedAddressSuggestions);
    }
  };

  const onSelectedOptionsChange = (
    newSelectedLocationOptions: Array<ExtendedMultiLocationSuggestOption>,
  ) => {
    const shouldScroll = newSelectedLocationOptions.length > 3;
    if (shouldScroll) {
      setTimeout(() => {
        inputRef.current?.scrollIntoView({ block: 'center' });
      }, 0);
    }
    setSelectedFiltersOptions({
      ...selectedFiltersOptions,
      searchLocations: newSelectedLocationOptions,
    });
  };

  const selectedSearchLocationsIsEmpty =
    !selectedSearchLocations || selectedSearchLocations.length === 0;

  const recentLocations = getLocationSearchRecentLocations();

  const showRecentLocations = addressSuggestions.length === 0;

  return (
    <>
      <StyledMultiLocationSuggest
        {...props}
        ref={refCallback}
        label="location-suggest"
        hideLabel
        autoFocus
        openOnFocus
        showRecentLocations={showRecentLocations}
        options={addressSuggestions.length > 0 ? addressSuggestions : recentLocations}
        selectedOptions={selectedSearchLocations}
        placeholder={selectedSearchLocationsIsEmpty ? 'Where are you looking?' : ''}
        disableListboxFlip
        mapOptionToListNode={customMapOptionToListNode}
        mapOptionsToListboxNode={mapOptionsToListboxNode}
        onInputChange={onInputChange}
        onSelectedOptionsChange={onSelectedOptionsChange}
        icon={icon}
        onKeyUp={onKeyPress}
        top={switchTop + (selectedSearchLocationsIsEmpty ? 0 : 60)}
        disableAutomaticHighlight
        mapOptionToString={(option) => option.display.text}
      />

      {!shouldHideSwitch &&
        selectedFiltersOptions.searchLocations &&
        selectedFiltersOptions.searchLocations.length > 0 && (
          <SwitchWrapper top={switchTop} showRecentLocations={showRecentLocations}>
            <StyledSwitch
              label="Include surrounding suburbs"
              checked={selectedFiltersOptions.includeSurroundingSuburbs}
              defaultChecked
              onChange={handleSurroundingSuburbOnChange}
              dangerouslySetClassNames={{
                label: 'label',
              }}
            />
          </SwitchWrapper>
      )}
    </>
  );
};
